import {
  BankOutlined,
  BuildOutlined,
  CarOutlined,
  CompassOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  ScheduleOutlined,
  ShopOutlined,
  TrophyOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  AutoComplete,
  Col,
  Form,
  Input,
  Row,
} from "antd";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import debounce from "lodash.debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import geoSearchService from "../../services/geoSearchService";

const FALLBACK_CENTER = [-28.4793, 24.6727];

const normalizeCenter = (center) => {
  if (!Array.isArray(center) || center.length < 2) {
    return FALLBACK_CENTER;
  }

  const [lat, lon] = center;
  const normalizedLat = Number(lat);
  const normalizedLon = Number(lon);

  if (Number.isNaN(normalizedLat) || Number.isNaN(normalizedLon)) {
    return FALLBACK_CENTER;
  }

  return [normalizedLat, normalizedLon];
};

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e);
    },
  });
  return null;
};

/**
 * GeoLookup - Lightweight geosearch component with map view and search autocomplete
 * * Data-driven component that takes suggestions and selection handling from parent.
 * This keeps the component focused on displaying map + search UI.
 * * @param {function} onSuggestionsNeeded - Callback to fetch suggestions. Receives: (query, provider) => Promise<suggestions[]>
 * If not provided, uses default backend fetch. Override to control suggestion retrieval.
 * @param {function} onSelectEntry - Callback when user selects a suggestion. Receives: (entry, provider, applyLocationToMap)
 * If not provided, uses default selection handling. Call applyLocationToMap to update the map.
 * @param {function} onDataUpdate - Callback when location is applied to map. Receives: { lat, lon, fullAddress, structured }
 * @param {array} defaultCenter - Default map center [lat, lon]. Default: [-28.4793, 24.6727]
 * @param {string} regionCode - Optional region code to bias search results (e.g. "za" for South Africa). Default: " " (no bias)
 * @param {string} defaultProvider - Provider name. Default: "nominatim"
 * @param {array} defaultSuggestions - Pre-populated suggestions (takes precedence over fetched suggestions)
 * @param {string} mapHeight - Height of the map container. Default: "100%"
 */
const GeoSearch = React.forwardRef(({
  onSuggestionsNeeded = null,
  onSelectEntry = null,
  onDataUpdate = () => {},
  defaultCenter = [-28.4793, 24.6727],
  regionCode = " ",
  defaultProvider = "nominatim",
  defaultSuggestions = [],
  mapHeight = "30vh",
  sessionToken=null,
}, ref) => {
  const normalizedDefaultCenter = useMemo(
    () => normalizeCenter(defaultCenter),
    [defaultCenter]
  );
  const [position, setPosition] = useState(normalizedDefaultCenter);
  const [address, setAddress] = useState({});
  const [options, setOptions] = useState([]);
  const mapRef = useRef(null);

  useEffect(() => {
    setPosition(normalizedDefaultCenter);
  }, [normalizedDefaultCenter]);
  
  // Convert backend suggestions → AntD options
  const mapToOptionsView = (suggestions = []) => {
    return suggestions.map((s, index) => {
      const uniqueKey = `${s.provider}-${s.id}-${index}`;
      const label = s.label || s.fullAddress || "Unknown";
      const mainText = s.label?.split(",")[0] || s.fullAddress?.split(",")[0] || "Unknown";
      const secondaryText = s.fullAddress || s.label || "";

      return {
        key: uniqueKey,
        value: label,
        feature: s,
        provider: s.provider,
        label: (
          <div
            key={uniqueKey}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "4px 0",
            }}
          >
            <div style={{ fontSize: "18px" }}>
              {getFeatureIcon(s.feature_type || s.type)}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                flex: 1,
              }}
            >
              <span style={{ fontWeight: 600, color: "#262626" }}>
                {mainText}
              </span>
              {secondaryText && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#8c8c8c",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                >
                  {secondaryText}
                </span>
              )}
            </div>

            <span
              style={{
                fontSize: "11px",
                color: "#1890ff",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {s.provider.toUpperCase()}
            </span>
          </div>
        ),
      };
    });
  };

  // Apply selection to map and call parent callback
  const applyLocationToMap = (lat, lon, structured) => {
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;

    const newCoords = [lat, lon];
    setPosition(newCoords);
    setAddress(structured);
    

    if (mapRef.current) {
      const maxZoom = mapRef.current.getMaxZoom();
      mapRef.current.flyTo(newCoords, maxZoom - 3, { duration: 1.5 });
    }

    // Call the callback to expose the data
    onDataUpdate({
      lat,
      lon,
      fullAddress: structured.fullAddress || structured.display_name,
      structured,
    });
  };

  // Fetch suggestions with optional override
  const fetchSuggestions = async (value) => {
    
    if (!value) {
      setOptions([]);
      return;
    }

    try {
      let suggestions = [];

      if (onSuggestionsNeeded) {
        // Use parent-provided suggestion fetcher
        suggestions = await onSuggestionsNeeded(defaultProvider, value, sessionToken, regionCode);
      } else {
        // Default backend fetch
        suggestions = await geoSearchService.fetchBackendAutocomplete(defaultProvider, value, sessionToken, regionCode);
      }
      
      setOptions(mapToOptionsView(suggestions));
    } catch (err) {
      console.error("Search error:", err);
      setOptions([]);
    }
  };

  const debouncedFetch = useMemo(
    () => debounce(fetchSuggestions, 400),
    [defaultProvider, onSuggestionsNeeded]
  );

  const handleSearch = (value) => {    
    debouncedFetch(value);
  };

  // Handle selection with optional override
  const handleSelect = async (_, option) => {
    const prov = option.provider || defaultProvider;
    const entry = option.feature;

    if (onSelectEntry) {
      // Use parent-provided selection handler
      await onSelectEntry(entry, prov, applyLocationToMap);
    } else {
      // Default selection handling
      if (prov === "nominatim") {
        applyLocationToMap(entry.lat, entry.lon, entry);
        return;
      }

      // Google / Mapbox require backend details
      const details = await geoSearchService.fetchBackendDetails(prov, entry.id, sessionToken);
      if (!details) return;

      applyLocationToMap(details.lat, details.lon, details);
    }
  };

  // Expose applyLocationToMap as a ref method for external calls
  React.useImperativeHandle(ref, () => ({
    applyLocation: (lat, lon, structured) => {
      applyLocationToMap(lat, lon, structured);
    },
  }));

  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;

    try {
      const result = await geoSearchService.fetchReverseGeocode(defaultProvider, lat, lng);
      if (result && !result.error) {
        applyLocationToMap(result.lat, result.lon, result);
      }
    } catch (err) {
      console.error("Map click reverse geocoding error:", err);
      applyLocationToMap(lat, lng, { display_name: "Selected Map Location" });
    }
  };

  return (
  <Row gutter={[0, 16]} style={{ width: "100%" }}>
    <Col span={24}>
      <div
        style={{
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Form.Item 
          label="" 
          wrapperCol={{ span: 24 }}
        >
          <AutoComplete
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search for places..."
              allowClear
            />
          </AutoComplete>
        </Form.Item>
      </div>
    </Col>
    <Col span={24}>
      <div style={{ width: "100%", overflow: "hidden", minHeight: 0 }}>
        <MapContainer
          ref={mapRef}
          center={position}
          zoom={5}
          style={{ height: mapHeight, width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position}>
            <Popup>
              {address.fullAddress ||
                address.display_name ||
                "Center of South Africa"}
            </Popup>
          </Marker>
          <MapInvalidator position={position} />
          <MapClickHandler onMapClick={handleMapClick} />
        </MapContainer>
      </div>
    </Col>
  </Row>
);
});

const getFeatureIcon = (type) => {
  if (!type) return <GlobalOutlined style={{ color: "#bfbfbf" }} />;
  
  const typeLower = type.toLowerCase();

  if (["poi", "shop", "mall", "retail", "store", "marketplace", "shopping_mall"].includes(typeLower)) {
    return <ShopOutlined style={{ color: "#1890ff" }} />;
  }
  if (["university", "educational_institution", "school", "library", "college", "academy", "institute"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#52c41a" }} />;
  }
  if (["hospital", "clinic", "health", "medical", "pharmacy", "healthcare"].includes(typeLower)) {
    return <MedicineBoxOutlined style={{ color: "#f5222d" }} />;
  }
  if (["industrial", "factory", "warehouse", "plant", "manufacturing"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#722ed1" }} />;
  }
  if (["park", "garden", "green_space", "sports", "sports_centre", "recreation", "playground"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#13c2c2" }} />;
  }
  if (["restaurant", "cafe", "food", "bar", "pub", "bistro", "bakery"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#fa8c16" }} />;
  }
  if (["museum", "art", "gallery", "theatre", "cinema", "cultural", "tourist_attraction", "event_venue"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#eb2f96" }} />;
  }
  if (["bank", "financial", "atm", "exchange"].includes(typeLower)) {
    return <BankOutlined style={{ color: "#13c2c2" }} />;
  }
  if (["sports_centre", "sports_complex", "gymnasium", "stadium", "fitness"].includes(typeLower)) {
    return <TrophyOutlined style={{ color: "#faad14" }} />;
  }
  if (["construction", "building", "office", "commercial", "workplace"].includes(typeLower)) {
    return <BuildOutlined style={{ color: "#bfbf00" }} />;
  }
  if (["transport", "parking", "transit", "station", "bus", "train", "railway"].includes(typeLower)) {
    return <CarOutlined style={{ color: "#1890ff" }} />;
  }
  if (["government", "public_services", "administration", "civic"].includes(typeLower)) {
    return <DatabaseOutlined style={{ color: "#1890ff" }} />;
  }
  if (["address", "residential", "apartment", "house", "housing"].includes(typeLower)) {
    return <HomeOutlined style={{ color: "#52c41a" }} />;
  }
  if (["research", "research_institute", "establishment", "service", "association_or_organization", "point_of_interest"].includes(typeLower)) {
    return <ScheduleOutlined style={{ color: "#722ed1" }} />;
  }
  if (["street", "neighborhood", "locality", "administrative"].includes(typeLower)) {
    return <EnvironmentOutlined style={{ color: "#faad14" }} />;
  }
  if (["postcode", "place", "city", "region", "country"].includes(typeLower)) {
    return <CompassOutlined style={{ color: "#722ed1" }} />;
  }

  return <GlobalOutlined style={{ color: "#bfbfbf" }} />;
};

// Helper component to handle map invalidation on render
const MapInvalidator = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      // Invalidate size after a short delay to ensure DOM has updated
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }
  }, [map]);

  return null;
};

GeoSearch.displayName = "GeoLookup";

export default GeoSearch;

